var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, Stack, TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Worktime } from '../../../enum';
import { useTerminologyTranslation } from '../../app/terminology';
import { Autocomplete, ErrorMessageBox, FallbackComponent, RadioGroup } from '../../common';
import { FormField, InputLabel } from '../../common/formField';
import { SelectOrCustomField } from '../../common/selectOrCustomField';
import { usePlanAPI, usePlanTypeCategory, useWizardParams } from '../selectors';
import { MultiskillWarningDialog } from './multiskillWarningDialog';
import { scopeAssignableLobs } from './queuesTable';
import { NEW_LOB_OPTION_ID, QUEUE_NAME_MAX_LENGTH, useCancelAndDeleteMultiskill, useCreateQueueAPI, useLanguageOptions, useLobsAPI, useQueuesAPI, useUpdateQueueAPI, useWorkTypeAPI } from './selectors';
import { DialogMode, WorkType } from './types';
var defaultValues = {
  businessArea: '',
  lobId: null,
  subLobId: null,
  sandboxLobName: '',
  sandboxSubLobName: '',
  worktypeId: WorkType.InboundCalls,
  languages: 'en',
  worktime: Worktime.NonDeferrable
};
export var AddQueueDialog = function (_a) {
  var _b, _c;
  var queueData = _a.queueData,
    open = _a.open,
    onClose = _a.onClose,
    mode = _a.mode;
  var t = useTerminologyTranslation(['wizard', 'translation']).t;
  var _d = useWorkTypeAPI(),
    workTypeData = _d[0],
    fetchWorkType = _d[1];
  var _e = useQueuesAPI(),
    fetchQueues = _e[1];
  var _f = useLobsAPI(),
    lobData = _f[0],
    fetchLob = _f[1];
  var planData = usePlanAPI()[0];
  var _g = useCreateQueueAPI(),
    createQueueData = _g[0],
    createQueue = _g[1],
    clearCreateQueueData = _g[2];
  var _h = useUpdateQueueAPI(queueData === null || queueData === void 0 ? void 0 : queueData.id),
    updateQueueData = _h[0],
    updateQueue = _h[1],
    clearUpdateQueueData = _h[2];
  var fetchCancelAndDeleteMultiskill = useCancelAndDeleteMultiskill();
  var _j = useWizardParams(),
    basePlanId = _j.basePlanId,
    planId = _j.id;
  var isUpdate = mode === DialogMode.Update;
  // Imported queues inherit their LOB from the business unit chosen at forecast import — not user-editable
  var isImportedQueue = isUpdate && !!(queueData === null || queueData === void 0 ? void 0 : queueData.isImported);
  var LANGUAGE_OPTIONS = useLanguageOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var NEW_OPTION = {
    id: NEW_LOB_OPTION_ID,
    name: t('queue.form.addQueue.new'),
    value: 'NEW'
  };
  // validate form in here
  var validateForm = yup.object().shape({
    businessArea: yup.string().required().max(24).normalizeSpace().quotesNotAllowed().label('Name'),
    // LOB assignment is optional for imported queues (user-entered, may be a per-plan sandbox value)
    lobId: isImportedQueue ? yup.string().nullable() : yup.string().required(),
    subLobId: yup.string().nullable(),
    sandboxLobName: yup.string().when('lobId', function (_a, schema) {
      var lobId = _a[0];
      return lobId === String(NEW_OPTION.id) ? schema.normalizeSpace().required().quotesNotAllowed() : schema;
    }),
    sandboxSubLobName: yup.string().when('subLobId', function (_a, schema) {
      var subLobId = _a[0];
      return subLobId === String(NEW_OPTION.id) ? schema.normalizeSpace().quotesNotAllowed() : schema;
    }),
    worktypeId: yup.number().integer().required(),
    languages: yup.string().required(),
    worktime: yup.number().integer().required()
  });
  var _k = useForm({
      defaultValues: defaultValues,
      resolver: yupResolver(validateForm)
    }),
    control = _k.control,
    handleSubmit = _k.handleSubmit,
    reset = _k.reset,
    watch = _k.watch,
    setValue = _k.setValue;
  var _l = useState(false),
    saveAndAdd = _l[0],
    setSaveAndAdd = _l[1];
  var _m = useState(false),
    showMultiskillWarning = _m[0],
    setShowMultiskillWarning = _m[1];
  var _o = useState(null),
    confirmAction = _o[0],
    setConfirmAction = _o[1];
  var handleMultiskillWarning = function (action) {
    setConfirmAction(function () {
      return action;
    });
    setShowMultiskillWarning(true);
  };
  // if update mode
  useEffect(function () {
    var _a, _b;
    if (!open) return;
    if (isUpdate && queueData) {
      var lobId_1 = queueData.lobId == null ? null : String(queueData.lobId);
      var subLobId_1 = queueData.subLobId == null ? null : String(queueData.subLobId);
      reset({
        businessArea: queueData.businessArea,
        lobId: lobId_1,
        subLobId: subLobId_1,
        worktypeId: (_a = queueData.worktype) === null || _a === void 0 ? void 0 : _a.id,
        languages: ((_b = queueData.languages) === null || _b === void 0 ? void 0 : _b[0]) || '',
        worktime: queueData.worktime
      });
    } else {
      reset(defaultValues);
    }
  }, [open, isUpdate, queueData, reset, NEW_OPTION.id]);
  // handle Lob - Sub Lob
  var lobId = watch('lobId');
  var subLobId = watch('subLobId');
  var newOptionStr = String(NEW_OPTION.id);
  var isNewLob = lobId === newOptionStr;
  var isNewSubLob = subLobId === newOptionStr;
  useEffect(function () {
    if (!open) return;
    fetchLob();
  }, [fetchLob, open]);
  // Scope assignable LOBs to the plan's data source (shared rule with the queues table).
  var businessUnitId = (_c = (_b = planData.data) === null || _b === void 0 ? void 0 : _b.businessUnitId) !== null && _c !== void 0 ? _c : null;
  var lobOptions = useMemo(function () {
    var _a;
    var scoped = scopeAssignableLobs((_a = lobData.data) !== null && _a !== void 0 ? _a : [], businessUnitId);
    var rawOptions = __spreadArray(__spreadArray([], scoped, true), [NEW_OPTION], false);
    return rawOptions.map(function (o) {
      return {
        id: String(o.id),
        name: o.lobName || o.name
      };
    });
  }, [NEW_OPTION, lobData.data, businessUnitId]);
  var lobSubOptions = useMemo(function () {
    var _a;
    var selectedLob = lobId ? (_a = lobData.data) === null || _a === void 0 ? void 0 : _a.find(function (lob) {
      return String(lob.id) === lobId;
    }) : null;
    var rawOptions = __spreadArray(__spreadArray([], (selectedLob === null || selectedLob === void 0 ? void 0 : selectedLob.subLobs) || [], true), [NEW_OPTION], false);
    return rawOptions.map(function (o) {
      return {
        id: String(o.id),
        name: o.subLobName || o.name
      };
    });
  }, [lobId, NEW_OPTION, lobData.data]);
  useEffect(function () {
    if (isNewSubLob) return;
    var isSelectedSubLobAvailable = subLobId == null || lobSubOptions.some(function (option) {
      return (option === null || option === void 0 ? void 0 : option.id) === subLobId;
    });
    if (!isSelectedSubLobAvailable) {
      setValue('subLobId', null);
    }
  }, [lobSubOptions, setValue, subLobId, isNewSubLob]);
  var planTypeCategory = usePlanTypeCategory();
  var worktypeId = watch('worktypeId');
  useEffect(fetchWorkType, [fetchWorkType]);
  var getWorktimeDisabled = useCallback(function (worktime) {
    return worktime === Worktime.Deferrable && (planTypeCategory === 'ProdHours' || [WorkType.InboundCalls, WorkType.Chat].includes(worktypeId));
  }, [worktypeId, planTypeCategory]);
  useEffect(function () {
    if (getWorktimeDisabled(Worktime.Deferrable)) {
      setValue('worktime', Worktime.NonDeferrable);
    }
  }, [getWorktimeDisabled, setValue]);
  useEffect(function () {
    if (open && (createQueueData.data || updateQueueData.data)) {
      fetchQueues();
      fetchLob();
      if (saveAndAdd && createQueueData.data) {
        reset(defaultValues);
      } else {
        onClose();
      }
    }
    return function () {
      clearCreateQueueData();
      clearUpdateQueueData();
      if (saveAndAdd) setSaveAndAdd(false);
    };
  }, [clearCreateQueueData, clearUpdateQueueData, createQueueData.data, fetchLob, fetchQueues, onClose, open, reset, saveAndAdd, updateQueueData.data]);
  // handle create/update queue
  var handleFormSubmit = function (values) {
    var _a, _b, _c, _d;
    var lobIdNum = values.lobId == null ? null : Number(values.lobId);
    var subLobIdNum = values.subLobId == null ? null : Number(values.subLobId);
    var isNewLobSubmit = lobIdNum === NEW_OPTION.id;
    var isNewSubLobSubmit = subLobIdNum === NEW_OPTION.id;
    var hasNewSubLobName = isNewSubLobSubmit && !!((_a = values.sandboxSubLobName) === null || _a === void 0 ? void 0 : _a.trim());
    var normalizedSubLobId = isNewSubLobSubmit || hasNewSubLobName ? null : subLobIdNum;
    var normalizedValues = __assign(__assign({}, values), {
      lobId: isNewLobSubmit ? null : lobIdNum,
      subLobId: normalizedSubLobId,
      sandboxLobName: isNewLobSubmit ? (_c = (_b = values.sandboxLobName) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : '' : '',
      sandboxSubLobName: hasNewSubLobName ? values.sandboxSubLobName.trim() : ''
    });
    var hasWorkTypeOrTimeChanged = !!queueData && (((_d = queueData.worktype) === null || _d === void 0 ? void 0 : _d.id) !== normalizedValues.worktypeId || queueData.worktime !== normalizedValues.worktime);
    var update = function () {
      return updateQueue({
        businessArea: normalizedValues.businessArea,
        lobId: normalizedValues.lobId,
        subLobId: normalizedValues.subLobId,
        worktypeId: normalizedValues.worktypeId,
        worktime: normalizedValues.worktime,
        languages: [normalizedValues.languages],
        sandboxLobName: normalizedValues.sandboxLobName,
        sandboxSubLobName: normalizedValues.sandboxSubLobName
      });
    };
    if (!isUpdate) {
      createQueue(__assign(__assign({}, normalizedValues), {
        languages: [normalizedValues.languages],
        basePlanId: basePlanId,
        planId: planId
      }));
      return;
    }
    var shouldDeleteMultiskill = (queueData === null || queueData === void 0 ? void 0 : queueData.hasMultiskill) && hasWorkTypeOrTimeChanged;
    if (shouldDeleteMultiskill) {
      handleMultiskillWarning(function () {
        fetchCancelAndDeleteMultiskill();
        update();
      });
    } else {
      update();
    }
  };
  return _jsx(Dialog, __assign({
    open: open,
    onClose: onClose
  }, {
    children: _jsxs(Box, __assign({
      component: "form",
      onSubmit: handleSubmit(handleFormSubmit)
    }, {
      children: [_jsx(DialogTitle, __assign({
        variant: "h2"
      }, {
        children: isUpdate ? t('queue.form.updateQueue.title') : t('queue.form.addQueue.title')
      })), _jsx(DialogContent, {
        children: _jsxs(FallbackComponent, __assign({
          requesting: !!createQueueData.requesting || !!updateQueueData.requesting,
          overlay: true
        }, {
          children: [_jsx(ErrorMessageBox, {
            errors: [createQueueData.error, updateQueueData.error]
          }), _jsxs(Stack, __assign({
            direction: "column",
            gap: 5
          }, {
            children: [_jsx(FormField, __assign({
              required: true,
              name: "businessArea",
              label: t('queue.form.addQueue.queueName'),
              control: control
            }, {
              children: _jsx(TextField, {
                autoFocus: true,
                placeholder: t('queue.form.businessArea.placeholder'),
                disabled: isUpdate && (queueData === null || queueData === void 0 ? void 0 : queueData.isImported),
                inputProps: {
                  maxLength: QUEUE_NAME_MAX_LENGTH
                }
              })
            })), _jsxs(Stack, __assign({
              direction: "row",
              gap: 5,
              justifyContent: "space-between"
            }, {
              children: [_jsxs(Stack, __assign({
                flex: 1
              }, {
                children: [_jsx(InputLabel, __assign({
                  required: !isImportedQueue
                }, {
                  children: t('queue.form.addQueue.lineOfBusiness')
                })), _jsx(SelectOrCustomField, {
                  required: !isImportedQueue,
                  isCustom: isNewLob,
                  control: control,
                  name: "lobId",
                  customName: "sandboxLobName",
                  options: lobOptions,
                  defaultValue: '',
                  placeholder: t('queue.form.addQueue.pleaseSelect'),
                  customPlaceholder: t('queue.form.addQueue.newLineOfBusiness'),
                  customValue: watch('sandboxLobName'),
                  onCustomChange: function (value) {
                    return setValue('sandboxLobName', value);
                  },
                  onSelectChange: function () {
                    return setValue('lobId', null);
                  },
                  onChange: function (value) {
                    if (value === newOptionStr) {
                      if (!isNewSubLob) {
                        setValue('subLobId', null);
                      }
                    } else {
                      setValue('sandboxLobName', '');
                      if (!isNewSubLob) {
                        setValue('sandboxSubLobName', '');
                      }
                    }
                  },
                  maxLength: QUEUE_NAME_MAX_LENGTH
                })]
              })), _jsxs(Stack, __assign({
                flex: 1
              }, {
                children: [_jsx(InputLabel, {
                  children: t('queue.form.addQueue.subLineOfBusiness')
                }), _jsx(SelectOrCustomField, {
                  isCustom: isNewSubLob,
                  control: control,
                  name: "subLobId",
                  customName: "sandboxSubLobName",
                  options: lobSubOptions,
                  defaultValue: '',
                  placeholder: t('queue.form.addQueue.pleaseSelect'),
                  customPlaceholder: t('queue.form.addQueue.newSubLineOfBusiness'),
                  customValue: watch('sandboxSubLobName'),
                  onCustomChange: function (value) {
                    return setValue('sandboxSubLobName', value);
                  },
                  onSelectChange: function () {
                    return setValue('subLobId', null);
                  },
                  disableClearable: false,
                  onChange: function (value) {
                    if (value !== newOptionStr) {
                      setValue('sandboxSubLobName', '');
                    }
                  },
                  maxLength: QUEUE_NAME_MAX_LENGTH
                })]
              }))]
            })), _jsxs(Stack, __assign({
              direction: "row",
              gap: 5,
              justifyContent: "space-between"
            }, {
              children: [_jsxs(Stack, __assign({
                flex: 1
              }, {
                children: [_jsx(InputLabel, __assign({
                  required: true
                }, {
                  children: t('queue.form.workType')
                })), _jsx(FormField, __assign({
                  name: "worktypeId",
                  control: control
                }, {
                  children: _jsx(Autocomplete, {
                    options: workTypeData.data,
                    placeholder: t('queue.form.addQueue.selectWorkType')
                  })
                }))]
              })), _jsxs(Stack, __assign({
                flex: 1
              }, {
                children: [_jsx(InputLabel, __assign({
                  required: true
                }, {
                  children: t('queue.form.addQueue.languages')
                })), _jsx(FormField, __assign({
                  name: "languages",
                  control: control
                }, {
                  children: _jsx(Autocomplete, {
                    options: LANGUAGE_OPTIONS,
                    getOptionValue: function (o) {
                      return o.code;
                    },
                    getOptionLabel: function (o) {
                      return o.name;
                    },
                    placeholder: t('queue.form.addQueue.enterLanguage')
                  })
                }))]
              }))]
            })), planTypeCategory !== 'ProdHours' && _jsx(FormField, __assign({
              name: "worktime",
              control: control
            }, {
              children: _jsxs(RadioGroup, __assign({
                row: true,
                defaultValue: Worktime.NonDeferrable
              }, {
                children: [_jsx(FormControlLabel, {
                  value: Worktime.NonDeferrable,
                  control: _jsx(Radio, {}),
                  label: t("queue.form.queueType.nonDeferrable")
                }), _jsx(FormControlLabel, {
                  value: Worktime.Deferrable,
                  control: _jsx(Radio, {}),
                  label: t("queue.form.queueType.deferrable"),
                  disabled: getWorktimeDisabled(Worktime.Deferrable)
                })]
              }))
            }))]
          }))]
        }))
      }), _jsxs(DialogActions, __assign({
        sx: {
          width: '100%'
        }
      }, {
        children: [_jsx(Button, __assign({
          variant: "outlined",
          onClick: onClose
        }, {
          children: t('translation:action.cancel')
        })), _jsxs(Stack, __assign({
          direction: "row",
          spacing: 2
        }, {
          children: [!isUpdate && _jsx(Button, __assign({
            variant: "outlined",
            type: "submit",
            onClick: function () {
              return setSaveAndAdd(true);
            }
          }, {
            children: t('translation:action.saveAdd')
          })), _jsx(Button, __assign({
            variant: "contained",
            type: "submit",
            onClick: function () {
              return setSaveAndAdd(false);
            }
          }, {
            children: t('translation:action.done')
          }))]
        }))]
      })), _jsx(MultiskillWarningDialog, {
        open: showMultiskillWarning,
        onClose: function () {
          setShowMultiskillWarning(false);
          setConfirmAction(null);
        },
        onConfirm: function () {
          if (confirmAction) confirmAction();
          setShowMultiskillWarning(false);
          setConfirmAction(null);
        }
      })]
    }))
  }));
};